export const M4_UNIT_15 = {
  id: "m4-u15-comparatives",
  title: "Unidad 15: Making Comparisons / Haciendo Comparaciones",
  topic: "Compare everything! Learn to compare cities, weather, prices, and more. Essential for making decisions, expressing opinions, and talking about differences. Master comparative adjectives to sound more natural!",
  iconType: "compare",
  grammarImage: "/grammar/grammar-m4-u15.jpg",
  grammarText: "COMPARATIVE ADJECTIVES: adjective + -ER + THAN (bigger than, faster than, colder than). MORE + adjective + THAN for long words (more expensive than, more beautiful than). Spelling rules: big→bigger (double consonant), easy→easier (y→i), nice→nicer (just add -r). IRREGULAR: good→better, bad→worse. Use with present simple for facts and opinions (Toronto IS bigger than Ottawa).",
  
  sets: [
    // SET 1: Introduction to Comparatives (-er than)
    {
      id: "m4-u15-set1",
      title: "Set 1: Basic Comparatives (-er than)",
      questions: [
        {
          type: "match",
          q: "Match adjective to comparative form:",
          pairs: [
            { left: "tall", right: "taller" },
            { left: "cold", right: "colder" },
            { left: "fast", right: "faster" }
          ],
          a: "completed",
          tip: "Pattern: adjective + ER! Tall → taller, cold → colder. Most one-syllable adjectives follow this!"
        },
        {
          type: "mc",
          q: "'Toronto is big. Montreal is big too. But Toronto is ___ than Montreal.' (más grande)",
          options: ["bigger", "big", "more big"],
          a: "bigger",
          tip: "BIGGER! One syllable = add -ER! Big → bigger. Use THAN after comparative!"
        },
        {
          type: "input",
          q: "Complete: 'Winter is ___ than fall.' (cold → comparative)",
          a: "colder"
        },
        {
          type: "order",
          q: "Ordena: 'Vancouver es más cálida que Toronto'",
          words: ["Vancouver", "is", "warmer", "than", "Toronto."],
          a: "Vancouver is warmer than Toronto."
        },
        {
          type: "mc",
          q: "Comparative structure: 'A is ___ than B'",
          options: [
            "adjective + ER + than",
            "adjective + than",
            "more + adjective"
          ],
          a: "adjective + ER + than",
          successtip: "✓ STRUCTURE! Pattern! 📐 adjective + ER + THAN! This is the basic formula for comparisons!"
        },
        {
          type: "input",
          q: "Complete: 'I am tall, but my brother is ___.' (taller)",
          a: "taller"
        },
        {
          type: "mc",
          q: "'A plane is ___ than a car.' (rápido)",
          options: ["faster", "fast", "more fast"],
          a: "faster",
          tip: "FASTER! Fast → faster! One syllable = add -ER! NOT 'more fast' ❌"
        },
        {
          type: "order",
          q: "Ordena: 'Hoy es más caluroso que ayer'",
          words: ["Today", "is", "hotter", "than", "yesterday."],
          a: "Today is hotter than yesterday.",
          successtip: "✓ COMPARING DAYS! Weather comparison! 🌡️ Hot → hotter (double the t). Useful for daily weather talk!"
        },
        {
          type: "input",
          q: "Complete: 'This hotel is ___ than that one.' (cheap → cheaper)",
          a: "cheaper"
        },
        {
          type: "mc",
          q: "Why use comparatives? To show:",
          options: [
            "Difference between two things",
            "Only one thing",
            "Three or more things"
          ],
          a: "Difference between two things",
          tip: "COMPARATIVES = 2 things! Show difference between A and B! 'This vs That'!"
        }
      ]
    },

    // SET 2: Spelling Rules for Comparatives
    {
      id: "m4-u15-set2",
      title: "Set 2: Spelling Rules",
      questions: [
        {
          type: "match",
          q: "Match adjective to spelling rule:",
          pairs: [
            { left: "big → bigger", right: "Double final consonant" },
            { left: "nice → nicer", right: "Already ends in E, just add -R" },
            { left: "easy → easier", right: "Y changes to I, add -ER" }
          ],
          a: "completed",
          tip: "Special spelling rules! Not all adjectives just add -ER! Learn these patterns!"
        },
        {
          type: "mc",
          q: "'Big' has one syllable and ends consonant-vowel-consonant. Comparative:",
          options: ["bigger (double the G)", "biger", "more big"],
          a: "bigger (double the G)",
          tip: "DOUBLE IT! Big → bigger, hot → hotter, sad → sadder! Short vowel + consonant = double!"
        },
        {
          type: "input",
          q: "Complete: 'Toronto is ___ than Ottawa.' (big → comparative)",
          a: "bigger"
        },
        {
          type: "order",
          q: "Ordena: 'Esta maleta es más grande que esa'",
          words: ["This", "suitcase", "is", "bigger", "than", "that", "one."],
          a: "This suitcase is bigger than that one."
        },
        {
          type: "mc",
          q: "'Nice' already ends in E. Comparative:",
          options: ["nicer (just add -R)", "niceer", "more nice"],
          a: "nicer (just add -R)",
          successtip: "✓ JUST ADD -R! Nice → nicer! 👍 When adjective ends in E, don't add -ER, just -R!"
        },
        {
          type: "input",
          q: "Complete: 'This weather is ___ than last week.' (nice → nicer)",
          a: "nicer"
        },
        {
          type: "mc",
          q: "'Easy' ends in Y. Comparative:",
          options: ["easier (Y → I + ER)", "easyer", "more easy"],
          a: "easier (Y → I + ER)",
          tip: "Y → I! Easy → easier, happy → happier, busy → busier! Change Y to I, add -ER!"
        },
        {
          type: "order",
          q: "Ordena: 'Este examen es más fácil que el anterior'",
          words: ["This", "test", "is", "easier", "than", "the", "last", "one."],
          a: "This test is easier than the last one.",
          successtip: "✓ EASIER! Perfect spelling! 📝 Easy → easier. This is a super common comparison word!"
        },
        {
          type: "input",
          q: "Complete: 'I am ___ today than yesterday.' (happy → comparative)",
          a: "happier"
        },
        {
          type: "mc",
          q: "Which is correct? 'This room is ___.'",
          options: [
            "bigger (double G)",
            "biger (one G)",
            "more big"
          ],
          a: "bigger (double G)",
          tip: "BIGGER! Always double! Short word + short vowel + consonant = double final letter!"
        }
      ]
    },

    // SET 3: Irregular Comparatives
    {
      id: "m4-u15-set3",
      title: "Set 3: Irregular Comparatives (Special Forms)",
      questions: [
        {
          type: "match",
          q: "Match irregular comparatives (these DON'T follow -ER rule):",
          pairs: [
            { left: "good", right: "better (NOT gooder!)" },
            { left: "bad", right: "worse (NOT badder!)" },
            { left: "far", right: "farther / further" }
          ],
          a: "completed",
          tip: "IRREGULAR! These don't follow rules! MEMORIZE: good→better, bad→worse! Very common!"
        },
        {
          type: "mc",
          q: "'This hotel is good, but that hotel is ___.' (mejor)",
          options: ["better", "gooder", "more good"],
          a: "better",
          tip: "BETTER! NOT 'gooder' ❌! Good → better = irregular! Most common irregular comparative!"
        },
        {
          type: "input",
          q: "Complete: 'This restaurant is ___ than the other one.' (good → better)",
          a: "better"
        },
        {
          type: "order",
          q: "Ordena: 'Esta opción es mejor que esa'",
          words: ["This", "option", "is", "better", "than", "that", "one."],
          a: "This option is better than that one."
        },
        {
          type: "mc",
          q: "'Yesterday's weather is bad. Today's weather is ___.' (peor)",
          options: ["worse", "badder", "more bad"],
          a: "worse",
          successtip: "✓ WORSE! Irregular! 👎 Bad → worse (NOT badder ❌). Second most common irregular comparative!"
        },
        {
          type: "input",
          q: "Complete: 'The traffic today is ___ than yesterday.' (bad → worse)",
          a: "worse"
        },
        {
          type: "mc",
          q: "'Far' has two comparative forms:",
          options: [
            "farther (distance) / further (degree)",
            "farer",
            "more far"
          ],
          a: "farther (distance) / further (degree)",
          tip: "TWO options! Farther = physical distance. Further = additional/more. Both work for distance!"
        },
        {
          type: "order",
          q: "Ordena: 'Vancouver está más lejos que Toronto'",
          words: ["Vancouver", "is", "farther", "than", "Toronto."],
          a: "Vancouver is farther than Toronto.",
          successtip: "✓ FARTHER! Distance! 🗺️ Farther = más lejos (physical distance). Further = además/más (degree)!"
        },
        {
          type: "input",
          q: "Complete: 'I think summer is ___ than winter.' (good → better)",
          a: "better"
        },
        {
          type: "mc",
          q: "REMEMBER: Good → ___, Bad → ___",
          options: ["better, worse", "gooder, badder", "more good, more bad"],
          a: "better, worse",
          tip: "MEMORIZE! These two are THE most important irregular comparatives! Use them constantly!"
        }
      ]
    },

    // SET 4: Long Adjectives (More + adjective)
    {
      id: "m4-u15-set4",
      title: "Set 4: 'More' for Long Adjectives",
      questions: [
        {
          type: "match",
          q: "Match long adjectives to comparative:",
          pairs: [
            { left: "expensive", right: "more expensive (NOT expensiver!)" },
            { left: "beautiful", right: "more beautiful" },
            { left: "interesting", right: "more interesting" }
          ],
          a: "completed",
          tip: "Long adjectives (3+ syllables) use MORE + adjective! Don't add -ER!"
        },
        {
          type: "mc",
          q: "'This hotel is expensive. That hotel is ___.' (más caro)",
          options: ["more expensive", "expensiver", "expensiver than"],
          a: "more expensive",
          tip: "MORE EXPENSIVE! Long word (3 syllables) = use MORE! NOT -ER! MORE + adjective + THAN!"
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is ___ expensive than Winnipeg.' (more/most)",
          a: "more"
        },
        {
          type: "order",
          q: "Ordena: 'París es más hermoso que Londres'",
          words: ["Paris", "is", "more", "beautiful", "than", "London."],
          a: "Paris is more beautiful than London."
        },
        {
          type: "mc",
          q: "When to use MORE + adjective:",
          options: [
            "Long adjectives (usually 3+ syllables)",
            "All adjectives",
            "Only short adjectives"
          ],
          a: "Long adjectives (usually 3+ syllables)",
          successtip: "✓ LONG WORDS! Rule! 📏 3+ syllables = use MORE! Beautiful, expensive, interesting = MORE!"
        },
        {
          type: "input",
          q: "Complete: 'I think summer is ___ interesting than winter.' (more)",
          a: "more"
        },
        {
          type: "mc",
          q: "'This museum is ___ than that one.' (interesante)",
          options: ["more interesting", "interestinger", "interestar"],
          a: "more interesting",
          tip: "MORE INTERESTING! 4 syllables = definitely use MORE! Can't add -ER to long words!"
        },
        {
          type: "order",
          q: "Ordena: 'Esta ciudad es más emocionante que mi ciudad'",
          words: ["This", "city", "is", "more", "exciting", "than", "my", "city."],
          a: "This city is more exciting than my city.",
          successtip: "✓ MORE EXCITING! Natural comparison! 🎉 3 syllables = MORE! Use for expressing opinions!"
        },
        {
          type: "input",
          q: "Complete: 'I think beaches are ___ relaxing than cities.' (more)",
          a: "more"
        },
        {
          type: "mc",
          q: "Some 2-syllable adjectives can use BOTH forms:",
          options: [
            "Quieter OR more quiet, Simpler OR more simple",
            "Only -ER",
            "Only MORE"
          ],
          a: "Quieter OR more quiet, Simpler OR more simple",
          tip: "2-syllable flexibility! Some 2-syllable words accept BOTH! 'Quieter' = 'more quiet'. Both correct!"
        }
      ]
    },

    // SET 5: Comparing People
    {
      id: "m4-u15-set5",
      title: "Set 5: Comparing People",
      questions: [
        {
          type: "match",
          q: "Match comparison to use:",
          pairs: [
            { left: "He is taller than me", right: "Height comparison" },
            { left: "She is older than him", right: "Age comparison" },
            { left: "I am faster than you", right: "Speed comparison" }
          ],
          a: "completed",
          tip: "Personal comparisons! Compare height, age, speed, etc. Very common in conversation!"
        },
        {
          type: "mc",
          q: "'I am 180cm. You are 170cm. I am ___ than you.'",
          options: ["taller", "tall", "more tall"],
          a: "taller",
          tip: "TALLER! Height comparison! 📏 Tall → taller! Super common personal comparison!"
        },
        {
          type: "input",
          q: "Complete: 'My sister is ___ than me.' (young → younger)",
          a: "younger"
        },
        {
          type: "order",
          q: "Ordena: 'Mi hermano es más alto que yo'",
          words: ["My", "brother", "is", "taller", "than", "me."],
          a: "My brother is taller than me."
        },
        {
          type: "mc",
          q: "'I am 25 years old. She is 30 years old. She is ___ than me.'",
          options: ["older", "old", "more old"],
          a: "older",
          successtip: "✓ OLDER! Age comparison! 🎂 Old → older! Very common for talking about age!"
        },
        {
          type: "input",
          q: "Complete: 'You speak English ___ than me!' (good → better)",
          a: "better"
        },
        {
          type: "mc",
          q: "'He works fast. She works very fast. She is ___ than him.'",
          options: ["faster", "fast", "more fast"],
          a: "faster",
          tip: "FASTER! Speed comparison! ⚡ Fast → faster! Common for work, sports, activities!"
        },
        {
          type: "order",
          q: "Ordena: 'Ella es más inteligente que él'",
          words: ["She", "is", "smarter", "than", "him."],
          a: "She is smarter than him.",
          successtip: "✓ SMARTER! Intelligence comparison! 🧠 Smart → smarter (one syllable, add -ER)!"
        },
        {
          type: "input",
          q: "Complete: 'I am ___ at cooking than my roommate.' (good → better)",
          a: "better"
        },
        {
          type: "mc",
          q: "Personal question: 'Are you taller than your best friend?'",
          options: [
            "YOUR answer! 'Yes, I am taller' OR 'No, I am shorter'",
            "Just yes/no",
            "Don't answer"
          ],
          a: "YOUR answer! 'Yes, I am taller' OR 'No, I am shorter'",
          tip: "EXPRESS YOURSELF! Personal comparison! Share YOUR information! Make it personal!"
        }
      ]
    },

    // SET 6: Comparing Canadian Cities
    {
      id: "m4-u15-set6",
      title: "Set 6: Comparing Places & Cities",
      questions: [
        {
          type: "match",
          q: "Match Canadian city comparison (based on facts):",
          pairs: [
            { left: "Toronto is bigger than Montreal", right: "3M vs 2M people" },
            { left: "Vancouver is warmer than Winnipeg", right: "West coast vs prairies" },
            { left: "Montreal is cheaper than Toronto", right: "Cost of living" }
          ],
          a: "completed",
          tip: "Real Canadian facts! Toronto = largest. Vancouver = mildest. Montreal = cheapest major city!"
        },
        {
          type: "mc",
          q: "'Toronto has 3 million people. Ottawa has 1 million. Toronto is ___.'",
          options: ["bigger than Ottawa", "smaller than Ottawa", "big"],
          a: "bigger than Ottawa",
          tip: "BIGGER! Population = size! Toronto is Canada's largest city! Facts-based comparison!"
        },
        {
          type: "input",
          q: "Complete: 'Vancouver is ___ than Toronto in winter.' (warm → warmer)",
          a: "warmer"
        },
        {
          type: "order",
          q: "Ordena: 'Montreal es más barato que Toronto'",
          words: ["Montreal", "is", "cheaper", "than", "Toronto."],
          a: "Montreal is cheaper than Toronto."
        },
        {
          type: "mc",
          q: "Cost of living in Canadian cities: 'Toronto is ___.'",
          options: [
            "more expensive than most Canadian cities",
            "the cheapest city",
            "cheaper than Winnipeg"
          ],
          a: "more expensive than most Canadian cities",
          successtip: "✓ MORE EXPENSIVE! Reality! 💰 Toronto & Vancouver = most expensive Canadian cities! Important for immigrants!"
        },
        {
          type: "input",
          q: "Complete: 'Winnipeg is ___ than Vancouver.' (cold → colder)",
          a: "colder"
        },
        {
          type: "mc",
          q: "'Vancouver's winter is mild. Winnipeg's winter is very cold. Vancouver is ___.'",
          options: ["warmer than Winnipeg", "colder than Winnipeg", "warm"],
          a: "warmer than Winnipeg",
          tip: "WARMER! Weather comparison! 🌡️ Vancouver = mildest Canadian winters. Winnipeg = coldest major city!"
        },
        {
          type: "order",
          q: "Ordena: 'Quebec es más francófono que Ontario'",
          words: ["Quebec", "is", "more", "French-speaking", "than", "Ontario."],
          a: "Quebec is more French-speaking than Ontario.",
          successtip: "✓ MORE FRENCH-SPEAKING! Cultural fact! 🇫🇷 Quebec = majority French. Long adjective = use MORE!"
        },
        {
          type: "input",
          q: "Complete: 'Calgary is ___ to the mountains than Toronto.' (close → closer)",
          a: "closer"
        },
        {
          type: "mc",
          q: "YOUR OPINION: 'Which Canadian city is more interesting - Toronto or Montreal?'",
          options: [
            "YOUR opinion! Explain WHY using comparatives!",
            "Both",
            "Neither"
          ],
          a: "YOUR opinion! Explain WHY using comparatives!",
          tip: "PERSONAL OPINION! Express yourself! 'I think Montreal is more interesting BECAUSE...'"
        }
      ]
    },

    // SET 7: Comparing Weather & Seasons
    {
      id: "m4-u15-set7",
      title: "Set 7: Weather & Season Comparisons",
      questions: [
        {
          type: "match",
          q: "Match seasonal comparison:",
          pairs: [
            { left: "Summer is hotter than spring", right: "Temperature" },
            { left: "Winter is colder than fall", right: "Cold weather" },
            { left: "Spring is rainier than winter", right: "Precipitation" }
          ],
          a: "completed",
          tip: "Seasonal patterns! Comparing weather = super common Canadian conversation!"
        },
        {
          type: "mc",
          q: "'Today is 25°C. Yesterday is 20°C. Today is ___.'",
          options: ["warmer than yesterday", "colder than yesterday", "warm"],
          a: "warmer than yesterday",
          tip: "WARMER! Daily weather comparison! 🌡️ Higher temp = warmer! Common daily talk!"
        },
        {
          type: "input",
          q: "Complete: 'Winter is ___ than summer.' (cold → colder)",
          a: "colder"
        },
        {
          type: "order",
          q: "Ordena: 'El verano es más caluroso que la primavera'",
          words: ["Summer", "is", "hotter", "than", "spring."],
          a: "Summer is hotter than spring."
        },
        {
          type: "mc",
          q: "'The forecast says tomorrow is ___.' (más frío que hoy)",
          options: ["colder than today", "more cold than today", "coldest"],
          a: "colder than today",
          successtip: "✓ COLDER! Forecast comparison! 📱 Using forecast + comparatives = natural planning talk!"
        },
        {
          type: "input",
          q: "Complete: 'Spring is ___ than winter in Canada.' (nice → nicer)",
          a: "nicer"
        },
        {
          type: "mc",
          q: "Canadian seasons: 'Summer is ___ than winter for outdoor activities.'",
          options: ["better", "gooder", "more good"],
          a: "better",
          tip: "BETTER! Irregular! ☀️ Good → better! Summer = better for outdoor activities (obviously!)!"
        },
        {
          type: "order",
          q: "Ordena: 'El otoño es más colorido que el verano'",
          words: ["Fall", "is", "more", "colorful", "than", "summer."],
          a: "Fall is more colorful than summer.",
          successtip: "✓ MORE COLORFUL! Beautiful comparison! 🍂 Fall leaves = colorful! Long adjective = MORE!"
        },
        {
          type: "input",
          q: "Complete: 'I think winter is ___ than summer.' (bad → worse)",
          a: "worse"
        },
        {
          type: "mc",
          q: "YOUR OPINION: 'Which season do you prefer?' Answer using comparatives:",
          options: [
            "'I prefer summer because it's warmer than winter'",
            "'Summer'",
            "'Yes'"
          ],
          a: "'I prefer summer because it's warmer than winter'",
          tip: "FULL ANSWER! Use comparatives to explain! 'I prefer X because it's [comparative] than Y'!"
        }
      ]
    },

    // SET 8: Travel & Vacation Comparisons
    {
      id: "m4-u15-set8",
      title: "Set 8: Comparing Travel Options",
      questions: [
        {
          type: "match",
          q: "Match travel comparison:",
          pairs: [
            { left: "Flying is faster than driving", right: "Speed" },
            { left: "Hotels are more expensive than hostels", right: "Cost" },
            { left: "Beaches are more relaxing than cities", right: "Experience" }
          ],
          a: "completed",
          tip: "Travel decisions! Compare options to make choices! Practical life skill!"
        },
        {
          type: "mc",
          q: "'The flight is $500. The bus ticket is $100. The flight is ___.'",
          options: ["more expensive than the bus", "cheaper than the bus", "expensive"],
          a: "more expensive than the bus",
          tip: "MORE EXPENSIVE! Price comparison! 💰 Long adjective = use MORE! Essential for budget decisions!"
        },
        {
          type: "input",
          q: "Complete: 'The train is ___ than the plane.' (slow → slower)",
          a: "slower"
        },
        {
          type: "order",
          q: "Ordena: 'Los hoteles son más caros que los albergues'",
          words: ["Hotels", "are", "more", "expensive", "than", "hostels."],
          a: "Hotels are more expensive than hostels."
        },
        {
          type: "mc",
          q: "'This hotel has wifi, breakfast, and a pool. That hotel has wifi only. This hotel is ___.'",
          options: ["better than that hotel", "worse than that hotel", "good"],
          a: "better than that hotel",
          successtip: "✓ BETTER! Value comparison! ⭐ More amenities = better! Practical travel decision-making!"
        },
        {
          type: "input",
          q: "Complete: 'I think beaches are ___ relaxing than cities.' (more)",
          a: "more"
        },
        {
          type: "mc",
          q: "'Road trips are ___ than flying.' (more + adjective = aventurero)",
          options: ["more adventurous", "adventurouser", "adventurous"],
          a: "more adventurous",
          tip: "MORE ADVENTUROUS! Long word! 🚗 4 syllables = definitely use MORE! Opinion expression!"
        },
        {
          type: "order",
          q: "Ordena: 'Viajar en temporada baja es más barato'",
          words: ["Traveling", "off-season", "is", "cheaper", "than", "peak", "season."],
          a: "Traveling off-season is cheaper than peak season.",
          successtip: "✓ CHEAPER! Budget tip! 💡 Off-season = cheaper! Practical travel advice using comparatives!"
        },
        {
          type: "input",
          q: "Complete: 'I think solo travel is ___ than group tours.' (good → better)",
          a: "better"
        },
        {
          type: "mc",
          q: "Making travel decisions: 'Which option is ___?' (key question)",
          options: [
            "better / cheaper / faster / more interesting?",
            "Yes or no?",
            "Where?"
          ],
          a: "better / cheaper / faster / more interesting?",
          tip: "'WHICH IS...?' Comparison questions! Essential for making decisions between options!"
        }
      ]
    },

    // SET 9: Comparison Idioms & Slang
    {
      id: "m4-u15-set9",
      title: "Set 9: Comparison Idioms & Expressions",
      questions: [
        {
          type: "match",
          q: "Match comparison idiom to meaning:",
          pairs: [
            { left: "hands down", right: "definitely better (sin duda)" },
            { left: "no contest", right: "one is obviously better (sin competencia)" },
            { left: "miles better", right: "much better (muchísimo mejor)" }
          ],
          a: "completed",
          tip: "Native expressions! Make comparisons sound more natural and emphatic!"
        },
        {
          type: "mc",
          q: "'This restaurant is better than that one, ___!' (idiom = definitivamente)",
          options: ["hands down", "hands up", "no hands"],
          a: "hands down",
          tip: "'HANDS DOWN'! Emphatic idiom! 👐 = sin duda/definitivamente mejor! 'Vancouver is hands down more beautiful!'"
        },
        {
          type: "input",
          q: "Complete: 'This hotel is better, no ___!' (contest = sin competencia)",
          a: "contest"
        },
        {
          type: "order",
          q: "Ordena: 'Este es definitivamente mejor que ese'",
          words: ["This", "is", "hands", "down", "better", "than", "that."],
          a: "This is hands down better than that."
        },
        {
          type: "mc",
          q: "'This place is ___ better!' (emphasis = muchísimo mejor)",
          options: ["miles", "kilometers", "feet"],
          a: "miles",
          successtip: "✓ MILES BETTER! Strong emphasis! 🎯 'Miles' = muchísimo (exaggeration). 'This is miles better than that!'"
        },
        {
          type: "input",
          q: "Complete: 'Comparing these two is like comparing apples and ___.' (idiom = too different)",
          a: "oranges"
        },
        {
          type: "mc",
          q: "'Apples and oranges' idiom means:",
          options: [
            "Things are too different to compare fairly",
            "Both are fruits",
            "Both are good"
          ],
          a: "Things are too different to compare fairly",
          tip: "'APPLES AND ORANGES'! Classic idiom! 🍎🍊 = son muy diferentes, no se pueden comparar justamente!"
        },
        {
          type: "order",
          q: "Ordena: 'Toronto está en otra categoría - es mucho mejor'",
          words: ["Toronto", "is", "in", "a", "different", "league", "-", "it's", "way", "better."],
          a: "Toronto is in a different league - it's way better.",
          successtip: "✓ DIFFERENT LEAGUE! Level idiom! 🏆 = en otra categoría (much better quality/level)!"
        },
        {
          type: "input",
          q: "Complete: 'This option is ___ better!' (way = mucho/muy)",
          a: "way"
        },
        {
          type: "mc",
          q: "'Way better' (slang) means:",
          options: [
            "Much better / a lot better (mucho mejor)",
            "A little better",
            "Not better"
          ],
          a: "Much better / a lot better (mucho mejor)",
          tip: "'WAY'! Emphasis slang! 'Way better' = mucho mejor! 'Way more expensive' = mucho más caro!"
        }
      ]
    },

    // SET 10: Making Decisions with Comparisons
    {
      id: "m4-u15-set10",
      title: "Set 10: Using Comparisons to Decide",
      questions: [
        {
          type: "match",
          q: "Match decision-making phrase:",
          pairs: [
            { left: "Which is better?", right: "Asking for comparison" },
            { left: "I think A is better than B", right: "Giving opinion" },
            { left: "It depends on...", right: "Conditional decision" }
          ],
          a: "completed",
          tip: "Decision language! Use comparisons to make choices and explain preferences!"
        },
        {
          type: "mc",
          q: "Asking for advice: '___ is better - Hotel A or Hotel B?'",
          options: ["Which", "What", "Where"],
          a: "Which",
          tip: "'WHICH'! Choice question! 'Which is better?' = ¿Cuál es mejor? Essential decision question!"
        },
        {
          type: "input",
          q: "Complete: 'I think Toronto is ___ than Montreal for jobs.' (good → better)",
          a: "better"
        },
        {
          type: "order",
          q: "Ordena: '¿Cuál ciudad es más barata para vivir?'",
          words: ["Which", "city", "is", "cheaper", "to", "live", "in?"],
          a: "Which city is cheaper to live in?"
        },
        {
          type: "mc",
          q: "Giving opinion with comparison: 'In my opinion, ___'",
          options: [
            "summer is better than winter",
            "summer is good",
            "I like summer"
          ],
          a: "summer is better than winter",
          successtip: "✓ OPINION + COMPARISON! Natural expression! 💭 'In my opinion' + comparative = explaining preference!"
        },
        {
          type: "input",
          q: "Complete: 'For me, beaches are ___ interesting than cities.' (more/less)",
          a: "more / less"
        },
        {
          type: "mc",
          q: "'It depends' - continuing: 'It depends on what's ___ for you - price or quality?'",
          options: ["more important", "important", "importance"],
          a: "more important",
          tip: "'DEPENDS ON'! Conditional! 🤔 'It depends on what's more important' = realistic decision-making!"
        },
        {
          type: "order",
          q: "Ordena: 'Depende de qué es más importante para ti'",
          words: ["It", "depends", "on", "what's", "more", "important", "for", "you."],
          a: "It depends on what's more important for you.",
          successtip: "✓ IT DEPENDS! Realistic phrase! ⚖️ Decisions depend on priorities! Comparisons help explain!"
        },
        {
          type: "input",
          q: "Complete: 'I prefer Montreal because it's ___ than Toronto.' (cheap → cheaper)",
          a: "cheaper"
        },
        {
          type: "mc",
          q: "Complete decision conversation:\n\nA: 'Should we fly or drive to Vancouver?'\nB: 'Flying is ___ than driving, but driving is ___. It depends on our budget.'\n\nCorrect comparatives:",
        options: [
          "faster / cheaper (practical decision-making)",
          "fast / cheap",
          "fastest / cheapest"
        ],
        a: "faster / cheaper (practical decision-making)",
        successtip: "✓ COMPLETE DECISION DIALOGUE! Real life! ✈️ vs 🚗 Using multiple comparisons to weigh options = advanced thinking!"
        }
      ]
    }
  ],
// ===================================================================
// READING SET: Toronto vs Montreal (The Great Comparison)
// ===================================================================
readingSet: {
  id: "m4-u15-reading",
  title: "Reading: Toronto vs Montreal",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Size & Language (preguntas 1 y 2)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Introduction to the Giants**

Are you planning to move to Canada? Two of the most popular cities for immigrants are Toronto and Montreal. But which one is better? Let's compare!

**SIZE**: Toronto is **bigger** than Montreal. Toronto has about 3 million people. Montreal has about 2 million people. Toronto is Canada's largest city.

**LANGUAGE**: Montreal is **more French-speaking** than Toronto. In Montreal, many people speak French. In Toronto, English is the main language. If you speak French, Montreal is better for you. If you prefer English, Toronto is easier.`
    },
    {
      type: "mc",
      q: "¿Cuántas categorías de comparación cubre el artículo en total?",
      options: [
        "Siete (Tamaño, Idioma, Costo, Clima, Trabajo, Cultura, Transporte)",
        "Tres",
        "Cinco"
      ],
      a: "Siete (Tamaño, Idioma, Costo, Clima, Trabajo, Cultura, Transporte)",
      successtip: "✓ DETAIL ORIENTED!\nHas identificado todos los puntos de análisis del texto. 📊"
    },
    {
      type: "mc",
      q: "Según el texto, ¿cuál ciudad es más grande en población?",
      options: [
        "Toronto (3M vs 2M personas)",
        "Montreal",
        "Tienen el mismo tamaño"
      ],
      a: "Toronto (3M vs 2M personas)",
      successtip: "✓ BIG CITY!\nToronto es la metrópolis más poblada de Canadá. 🏙️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Cost of Living & Weather (preguntas 3, 4 y 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Money and Temperature**

**COST OF LIVING**: Montreal is **MUCH cheaper** than Toronto! Rent in Montreal is about 30-40% cheaper than Toronto. Food is cheaper. Everything is cheaper! For budget-conscious people, Montreal is **hands down** better!

**WEATHER**: Both cities are cold in winter, but Montreal is **colder** than Toronto! Montreal has more snow. Toronto's winter is a bit **milder**. If you hate cold weather, Toronto is better.`
    },
    {
      type: "mc",
      q: "¿Qué tan barato es el alquiler (rent) en Montreal comparado con Toronto?",
      options: [
        "Un 30-40% más barato que en Toronto",
        "Un 10% más barato",
        "Es más caro que en Toronto"
      ],
      a: "Un 30-40% más barato que en Toronto",
      successtip: "✓ BUDGET SMART!\nMontreal es famosa por ser mucho más asequible. 💰"
    },
    {
      type: "mc",
      q: "¿Qué significa la expresión 'hands down' en el artículo?",
      options: [
        "Definitivamente / sin duda alguna",
        "Tal vez",
        "Realmente no"
      ],
      a: "Definitivamente / sin duda alguna",
      successtip: "✓ IDIOM!\n'Hands down' se usa para algo que es obvio o indiscutible. 🏆"
    },
    {
      type: "mc",
      q: "¿Cuál de las dos ciudades es más fría en invierno?",
      options: [
        "Montreal (más nieve y más frío que Toronto)",
        "Toronto",
        "Tienen la misma temperatura"
      ],
      a: "Montreal (más nieve y más frío que Toronto)",
      successtip: "✓ FREEZING!\nMontreal está más al norte y recibe mucha más nieve. ❄️"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Jobs & Culture (pregunta 6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Careers and Vibes**

**JOBS**: Toronto has **more job opportunities** than Montreal, especially in business and technology. Many big companies are in Toronto. The job market is **bigger** and **more diverse**.

**CULTURE**: Montreal is **more European** than Toronto. Montreal has beautiful old buildings, amazing food culture, and lots of festivals. Toronto is **more multicultural** and diverse.`
    },
    {
      type: "mc",
      q: "En términos de oportunidades laborales, ¿cuál ciudad es mejor según el texto?",
      options: [
        "Toronto (mercado laboral más grande y diverso)",
        "Montreal",
        "Son iguales"
      ],
      a: "Toronto (mercado laboral más grande y diverso)",
      successtip: "✓ CAREER GROWTH!\nToronto es el centro financiero y tecnológico de Canadá. 💼"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Conclusion & Idioms (preguntas 7, 8, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Final Verdict**

So which is better? **It depends!** If you want cheaper living and French culture, Montreal is better. If you want more jobs and English, Toronto is better. 

It's not comparing **apples and oranges**—both are good options, just different!`
    },

    

    {
      type: "mc",
      q: "La frase 'It depends' aparece en el artículo. ¿Qué quiere decir?",
      options: [
        "No hay una sola respuesta; depende de tus prioridades",
        "Toronto es definitivamente mejor",
        "Montreal es definitivamente mejor"
      ],
      a: "No hay una sola respuesta; depende de tus prioridades",
      successtip: "✓ BALANCED VIEW!\nLa elección depende de lo que cada persona necesite. ⚖️"
    },
    {
      type: "mc",
      q: "¿Qué significa el modismo 'apples and oranges' en el artículo?",
      options: [
        "Que ambas ciudades son buenas pero diferentes (no se pueden comparar injustamente)",
        "Que ambas ciudades producen frutas",
        "Que ambas ciudades son malas opciones"
      ],
      a: "Que ambas ciudades son buenas pero diferentes (no se pueden comparar injustamente)",
      successtip: "✓ IDIOM MASTER!\nSe usa para decir que dos cosas son demasiado diferentes para compararlas de forma directa. 🍎🍊"
    },
    {
      type: "mc",
      q: "¿Cuántos adjetivos comparativos identificas aproximadamente?",
      options: [
        "Muchos (bigger, cheaper, colder, better, more, older, etc.)",
        "Solo 2 o 3",
        "Ninguno"
      ],
      a: "Muchos (bigger, cheaper, colder, better, more, older, etc.)",
      successtip: "✓ GRAMMAR WATCH!\nEl artículo es un excelente ejemplo de cómo usar comparativos en inglés. 🎯"
    },
    {
      type: "mc",
      q: "¿Cuál es el propósito principal del artículo?",
      options: [
        "Ayudar al lector a comparar y decidir según sus propias prioridades",
        "Decir que Toronto es la mejor ciudad sin duda",
        "Convencer a todos de mudarse a Montreal"
      ],
      a: "Ayudar al lector a comparar y decidir según sus propias prioridades",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Has analizado un texto complejo sobre geografía y estilo de vida canadiense. 🏆"
    }
  ]
},
  // SET 12: Unit Review
  reviewSet: {
    id: "m4-u15-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match comparative type to example:",
        pairs: [
          { left: "Short adjective + ER", right: "bigger, colder, faster" },
          { left: "MORE + long adjective", right: "more expensive, more beautiful" },
          { left: "Irregular", right: "better, worse, farther" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Basic comparative structure:",
        options: [
          "A is [comparative] than B",
          "A is [adjective] than B",
          "A than B is [comparative]"
        ],
        a: "A is [comparative] than B"
      },
      {
        type: "mc",
        q: "Spelling: big → ?",
        options: ["bigger (double G)", "biger", "more big"],
        a: "bigger (double G)"
      },
      {
        type: "input",
        q: "Complete: Good → ___, Bad → ___",
        a: "better, worse"
      },
      {
        type: "input",
        q: "Complete: 'This hotel is ___ expensive than that one.' (more/most)",
        a: "more"
      },
      {
        type: "order",
        q: "Ordena: 'Toronto es más grande que Montreal'",
        words: ["Toronto", "is", "bigger", "than", "Montreal."],
        a: "Toronto is bigger than Montreal."
      },
      {
        type: "mc",
        q: "When to use MORE + adjective:",
        options: [
          "Long adjectives (3+ syllables)",
          "All adjectives",
          "Never"
        ],
        a: "Long adjectives (3+ syllables)"
      },
      {
        type: "mc",
        q: "'Hands down' means:",
        options: ["Definitely / without question", "Maybe", "Not at all"],
        a: "Definitely / without question"
      },
      {
        type: "mc",
        q: "'Which is better?' is used to:",
        options: [
          "Ask for comparison between two options",
          "Ask about one thing",
          "Ask about three things"
        ],
        a: "Ask for comparison between two options"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Complete realistic conversation:\n\nYou: 'I can't decide between Toronto and Montreal!'\nFriend: 'Let me help! Toronto is ___ than Montreal, and it has ___ jobs. But Montreal is MUCH ___ than Toronto!'\nYou: 'Which is ___ for winter?'\nFriend: 'Toronto is ___ than Montreal in winter. Montreal is ___!'\nYou: 'Hmm. For me, cost is ___ important than weather. I think Montreal is ___ for me!'\nFriend: 'Great! Montreal is ___ better for budget-conscious people!'\n\nCorrect comparatives:",
        options: [
          "bigger / more / cheaper / better / warmer / colder / more / better / hands down",
          "big / many / cheap / good / warm / cold / important / good / definitely",
          "biggest / most / cheapest / best / warmest / coldest / most important / best / very"
        ],
        a: "bigger / more / cheaper / better / warmer / colder / more / better / hands down",
        successtip: "✓ UNIT 15 MASTERED! Comparatives complete! 🎉 You can now: form comparatives correctly (-ER vs MORE), spell them right (bigger, easier, nicer), use irregulars (better, worse), compare people/places/things, use comparison idioms (hands down, miles better, apples and oranges), ask 'Which is better?', make decisions using comparisons, express opinions naturally! From basic 'bigger than' to complex decision-making conversations! You're comparing like a native speaker! 🇨🇦⚖️📊"
      }
    ]
  }
};
